import { IAdiRegistrationSetting, ICommonSettings } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { ReactNode } from "react";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IConfigAdiRegistrationSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingAdiRegistrationSetting?: IAdiRegistrationSetting;
}

export interface IConfigAdiRegistrationSettingsProps {
  formInputName: string[];
  closeModal: () => void;
  showModal: boolean;
  editingAdiRegistrationId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

interface IAdiRegistrationFormData {
  name: string;
  snippet: string;
}

export class ConfigAdiRegistrationSettings extends React.PureComponent<
  IConfigAdiRegistrationSettingsProps,
  IConfigAdiRegistrationSettingsState
> {
  formRef: React.RefObject<FormInstance> = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigAdiRegistrationSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle",
      editingAdiRegistrationSetting: undefined
    };
  }

  componentDidMount(): void {
    this.loadSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigAdiRegistrationSettingsProps): void {
    if (prevProps.editingAdiRegistrationId && !this.props.editingAdiRegistrationId) {
      this.formRef.current?.resetFields();
    }
    if (prevProps.editingAdiRegistrationId !== this.props.editingAdiRegistrationId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    }
  }

  loadSetting = (): void => {
    if (this.props.editingAdiRegistrationId) {
      const setting: IAdiRegistrationSetting | undefined = this.props.commonSettings.adiRegistrationSettings?.find(
        (x: IAdiRegistrationSetting) => x.id === this.props.editingAdiRegistrationId
      );

      this.setState({ editingAdiRegistrationSetting: setting }, () => {
        if (setting) {
          this.formRef.current?.resetFields();
          this.formRef.current?.setFieldsValue({
            name: setting.name,
            snippet: setting.snippet
          });
        }
      });
    } else {
      this.formRef.current?.resetFields();
    }
  };

  existingNameValidator = async (_rule: unknown, value: string): Promise<void> => {
    const settings: IAdiRegistrationSetting[] | null | undefined = this.props.commonSettings?.adiRegistrationSettings;
    const isSettingAlreadyExist: boolean =
      settings?.some((x: IAdiRegistrationSetting) => x.name === value && !this.props.editingAdiRegistrationId) ||
      value === "Default";
    if (isSettingAlreadyExist) throw new Error("ADI Snippet Setting Name already exists.");
  };

  onFinish = (data: IAdiRegistrationFormData): void => {
    this.saveStatus.start();

    const obj: IAdiRegistrationSetting = {
      id: Guid.create().toString(),
      name: data.name,
      snippet: data.snippet ?? ""
    };
    if (this.props.editingAdiRegistrationId) {
      obj.id = this.props.editingAdiRegistrationId;
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "adiRegistrationSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "adiRegistrationSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (): void => {};

  deleteSetting = (): void => {
    if (!this.props.editingAdiRegistrationId) {
      this.props.closeModal();
      return;
    }
    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingAdiRegistrationId || "",
      configKey: "adiRegistrationSettings",
      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`ADI Snippet setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render(): ReactNode {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="ADI Snippet"
        secondHeader="ADI Snippet"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingAdiRegistrationId ? this.deleteSetting : undefined}
            deleteTestId="adiRegistrationDeleteButton"
            submitTestId={`adiRegistration${this.props.editingAdiRegistrationId ? "Update" : "Create"}Button`}
            submitLabel={this.props.editingAdiRegistrationId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configAdiRegistrationForm"
          />
        }
      >
        <Form
          id="configAdiRegistrationForm"
          name="adiRegistrationSetting"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
        >
          <ConfigModalBody
            description={this.props.getSectionDescription(
              "android_playstoresettings_adiregistrationsnippet_description"
            )}
          >
            <div>
              <ConfigField
                label="Name"
                required
                tooltipContentKey="android_playstoresettings_adiregistrationsnippet_name"
              >
                <Form.Item
                  name="name"
                  rules={[
                    { required: true, message: "Please enter an ADI Snippet Setting Name." },
                    {
                      pattern: /^[A-Za-z][A-Za-z0-9 ]+$/,
                      message: "ADI Snippet Setting Name is invalid"
                    },
                    { validator: this.existingNameValidator }
                  ]}
                >
                  <KuikaInput size="md" data-testid="adiRegistrationNameInput" autoFocus />
                </Form.Item>
              </ConfigField>
              <ConfigField
                label="Snippet"
                required
                tooltipContentKey="android_playstoresettings_adiregistrationsnippet_snippet"
              >
                <Form.Item
                  name="snippet"
                  rules={[{ required: true, message: "Please paste the snippet from Google Play Console." }]}
                >
                  <KuikaInput
                    type="textarea"
                    size="md"
                    rows={8}
                    data-testid="adiRegistrationSnippetTextarea"
                    placeholder="Paste the snippet provided by Google Play Console"
                  />
                </Form.Item>
              </ConfigField>
            </div>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
